import { useState } from 'react';
import { Box, Button, Dialog } from '@cere/cere-design-system';
import type { Scope } from '@cef-ai/vault-sdk';
import { useVaultSession } from '@/src/state/session';

interface Props {
  open: boolean;
  scope: Scope;
  agentCount: number;
  onClose: () => void;
}

export function DeleteScopeDialog({ open, scope, agentCount, onClose }: Props) {
  const session = useVaultSession();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDefault = scope.name === 'default';
  const hasAgents = agentCount > 0;
  const blocked = isDefault || hasAgents;

  async function handleSubmit() {
    if (blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      await (await session.signingHandle()).scopes.delete(scope.name);
      await session.refreshScopes();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title={`Delete ${scope.displayName ?? scope.name}?`}
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={blocked || submitting}
            sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </>
      }
    >
      <Box sx={{ py: 1 }}>
        {isDefault ? (
          <>The default scope is protected and cannot be deleted.</>
        ) : hasAgents ? (
          <>This scope has {agentCount} connected agent{agentCount === 1 ? '' : 's'}. Disconnect agents from this scope first.</>
        ) : (
          <>This action cannot be undone. The scope and its event/cubby namespace will be removed.</>
        )}
        {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}
      </Box>
    </Dialog>
  );
}
